export default {
  getCroppedFile,
};

// cap the longer edge of the exported image, in pixels, so uploads stay a reasonable size
const MAX_DIMENSION = 1200;

/**
 * Draws the cropped region of a loaded <img> element onto a canvas, downscaling it if
 * needed to stay within MAX_DIMENSION. Accounts for
 * the ratio between the image's displayed size and its natural (full-resolution) size.
 * Exports back as either PNG or JPG and appends the matching extension to fileName.
 * @param {HTMLImageElement} image - the source image element (already loaded in the DOM)
 * @param {Object} crop - pixel crop relative to the displayed image size ({ x, y, width, height })
 * @param {string} fileName - base name (no extension) to give the resulting File
 * @param {string} fileType - MIME type of the passed in image ('image/png' or 'image/jpeg')
 * @returns {Promise<File>} the cropped image as a PNG or JPEG File
 */
function getCroppedFile(image, crop, fileName, fileType) {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const sourceX = crop.x * scaleX;
  const sourceY = crop.y * scaleY;
  const sourceWidth = crop.width * scaleX;
  const sourceHeight = crop.height * scaleY;

  const downscale = Math.min(
    1,
    MAX_DIMENSION / Math.max(sourceWidth, sourceHeight)
  );
  const outputWidth = Math.round(sourceWidth * downscale);
  const outputHeight = Math.round(sourceHeight * downscale);

  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    outputWidth,
    outputHeight
  );

  const extension = fileType === 'image/png' ? 'png' : 'jpg';
  const outputFileName = `${fileName}.${extension}`;

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to generate cropped image'));
        return;
      }
      resolve(new File([blob], outputFileName, { type: fileType }));
    }, fileType);
  });
}
